import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  CornerDownRight,
  CornerLeftUp,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router'
import { Menu, MenuItem, MenuSeparator } from '../ui'
import { useWatchlist } from '../../contexts/WatchlistContext'
import { useToast } from '../../contexts/ToastContext'
import type { WatchlistList } from '../../services/watchlist'
import { flattenWatchlist } from '../../services/watchlist'
import WatchlistCreateListInline from './WatchlistCreateListInline'
import {
  parseWatchlistListFilter,
  serialiseWatchlistListFilter,
  type WatchlistListFilterId,
} from '../../utils/watchlistListFilter'
import {
  dropPositionAt,
  listFamilyItems,
  listMoveTargets,
  listTreeRows,
  moveList as moveListInTree,
  type ListDropPosition,
  type ListTreeRow,
} from '../../utils/watchlistTree'

/** Pixels per nesting level. */
const INDENT = 14
const COLLAPSED_KEY = 'xosa-origin:watchlist-collapsed-lists'

function readCollapsed(): Set<number> {
  try {
    const raw = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]')
    return new Set(
      Array.isArray(raw) ? raw.filter(n => typeof n === 'number') : []
    )
  } catch {
    return new Set()
  }
}

function saveCollapsed(ids: Set<number>) {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...ids]))
  } catch {
    // Folding is a convenience; without storage it just resets.
  }
}

interface DropTarget {
  /** null = end of the top level. */
  targetId: number | null
  position: ListDropPosition
}

/**
 * List filter navigation in the account sidebar drill-down (/account/watchlist).
 * Lists form a tree: drag a list onto the top or bottom edge of another to
 * place it before or after, onto its middle to nest it, below the last list
 * to put it last at the top level. The »…« menu offers the same moves from
 * the keyboard.
 */
const WatchlistListNav: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { data, lists, createList, renameList, deleteList, moveList } =
    useWatchlist()
  const { showToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  const filter = parseWatchlistListFilter(searchParams.get('list'))
  const uniqueItems = useMemo(() => flattenWatchlist(data), [data])
  const uncategorizedCount = data.uncategorized.length
  const totalCount = uniqueItems.length

  const setFilter = (id: WatchlistListFilterId) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        const v = serialiseWatchlistListFilter(id)
        if (v === null) params.delete('list')
        else params.set('list', v)
        return params
      },
      { replace: true }
    )
  }

  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drag, setDrag] = useState<{ id: number; x: number; y: number } | null>(
    null
  )
  const [drop, setDrop] = useState<DropTarget | null>(null)
  const dragId = drag?.id ?? null
  const navRef = useRef<HTMLElement>(null)
  const suppressClick = useRef(false)

  const rows = useMemo(() => listTreeRows(lists), [lists])
  const nested = rows.some(r => r.depth > 0)
  const counts = useMemo(
    () => new Map(lists.map(l => [l.id, listFamilyItems(lists, l.id).length])),
    [lists]
  )
  const visibleRows = useMemo(() => {
    const out: ListTreeRow[] = []
    let hiddenBelow: number | null = null
    for (const row of rows) {
      if (hiddenBelow !== null && row.depth > hiddenBelow) continue
      hiddenBelow = null
      out.push(row)
      if (row.hasChildren && collapsed.has(row.list.id)) hiddenBelow = row.depth
    }
    return out
  }, [rows, collapsed])

  const setFolded = (id: number, folded: boolean) => {
    setCollapsed(prev => {
      if (prev.has(id) === folded) return prev
      const next = new Set(prev)
      if (folded) next.add(id)
      else next.delete(id)
      saveCollapsed(next)
      return next
    })
  }

  // Pointer handlers live on window for the length of a drag; they read
  // the latest tree from here.
  const latest = useRef({ lists, rows, collapsed })
  useEffect(() => {
    latest.current = { lists, rows, collapsed }
  }, [lists, rows, collapsed])

  async function handleMove(
    listId: number,
    targetId: number | null,
    position: ListDropPosition
  ) {
    try {
      const moved = await moveList(listId, targetId, position)
      if (!moved) {
        showToast('Listen kan ikke flyttes dertil.', { tone: 'signal' })
        return
      }
      if (position === 'inside' && targetId !== null) setFolded(targetId, false)
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  /** Where the dragged list would land under the pointer, if anywhere. */
  function dropTargetAt(x: number, y: number, id: number): DropTarget | null {
    const { lists, rows, collapsed } = latest.current
    const valid = (targetId: number | null, position: ListDropPosition) => {
      const next = moveListInTree(lists, id, targetId, position)
      return next !== null && next !== lists
    }
    const hit = document.elementFromPoint?.(x, y)
    if (!hit || !navRef.current?.contains(hit)) return null
    if (hit.closest('[data-list-drop-end]')) {
      return valid(null, 'after') ? { targetId: null, position: 'after' } : null
    }
    const rowEl = hit.closest<HTMLElement>('[data-list-id]')
    const row = rows.find(r => String(r.list.id) === rowEl?.dataset.listId)
    if (!rowEl || !row) return null
    const rect = rowEl.getBoundingClientRect()
    const position = dropPositionAt(y - rect.top, rect.height, {
      canNest: valid(row.list.id, 'inside'),
      expandedParent: row.hasChildren && !collapsed.has(row.list.id),
    })
    return position && valid(row.list.id, position)
      ? { targetId: row.list.id, position }
      : null
  }

  /**
   * Press and move a list row to drag it (on touch: hold it still first,
   * so a swipe still scrolls). Esc cancels.
   */
  function startPointerDrag(e: React.PointerEvent<HTMLElement>, id: number) {
    if (e.button > 0 || renamingId !== null) return
    if ((e.target as HTMLElement).closest('[aria-haspopup], [aria-expanded]'))
      return
    const touch = e.pointerType === 'touch'
    const start = { x: e.clientX, y: e.clientY }
    let started = false
    let target: DropTarget | null = null

    const begin = (x: number, y: number) => {
      started = true
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'grabbing'
      setDrag({ id, x, y })
    }
    const holdTimer = touch
      ? window.setTimeout(() => begin(start.x, start.y), 350)
      : undefined

    const onMove = (ev: PointerEvent) => {
      if (!started) {
        const distance = Math.hypot(ev.clientX - start.x, ev.clientY - start.y)
        if (touch) {
          if (distance > 8) stop()
          return
        }
        if (distance < 5) return
        begin(ev.clientX, ev.clientY)
      }
      target = dropTargetAt(ev.clientX, ev.clientY, id)
      setDrag({ id, x: ev.clientX, y: ev.clientY })
      setDrop(target)
    }
    const onUp = () => {
      const at = target
      const wasDragging = started
      stop()
      if (!wasDragging) return
      // The release would otherwise click the filter button under it.
      suppressClick.current = true
      window.setTimeout(() => {
        suppressClick.current = false
      }, 0)
      if (at) void handleMove(id, at.targetId, at.position)
    }
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') stop()
    }
    // While dragging on touch: no scrolling, no long-press menu.
    const holdStill = (ev: Event) => {
      if (started) ev.preventDefault()
    }
    const stop = () => {
      window.clearTimeout(holdTimer)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', stop)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('touchmove', holdStill)
      window.removeEventListener('contextmenu', holdStill)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
      target = null
      setDrag(null)
      setDrop(null)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', stop)
    window.addEventListener('keydown', onKey)
    window.addEventListener('touchmove', holdStill, { passive: false })
    window.addEventListener('contextmenu', holdStill)
  }

  async function handleCreate(name: string) {
    try {
      const list = await createList(name)
      showToast('Liste oprettet.')
      return list
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
      throw err
    }
  }

  async function handleDelete(list: WatchlistList) {
    const hasSublists = lists.some(l => l.parentId === list.id)
    if (
      !window.confirm(
        hasSublists
          ? `Slet listen «${list.name}»? Underlisterne rykker et niveau op. Entiteter, der kun er på listen, bliver uden liste, ikke slettet.`
          : `Slet listen «${list.name}»? Entiteterne bliver uden liste, ikke slettet.`
      )
    ) {
      return
    }
    try {
      await deleteList(list.id)
      showToast('Liste slettet.')
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Fejl', { tone: 'signal' })
    }
  }

  return (
    <nav
      ref={navRef}
      aria-label="Watchlist-lister"
      className="w-full"
      onClickCapture={e => {
        if (!suppressClick.current) return
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <button
        type="button"
        onClick={onBack}
        className="group inline-flex items-center gap-1 text-[13px] font-semibold text-ink-500 hover:text-ink transition-colors mb-4"
      >
        <ChevronLeft
          className="h-3.5 w-3.5 opacity-70 transition-transform duration-200 group-hover:-translate-x-0.5"
          aria-hidden
        />
        Indstillinger
      </button>

      <ul className="space-y-0.5">
        <li>
          <FilterButton
            active={filter === 'all'}
            count={totalCount}
            onClick={() => setFilter('all')}
            twisty={nested}
          >
            Alle
          </FilterButton>
        </li>
        <li>
          <FilterButton
            active={filter === 'uncategorized'}
            count={uncategorizedCount}
            onClick={() => setFilter('uncategorized')}
            twisty={nested}
          >
            Uden liste
          </FilterButton>
        </li>
        {lists.length > 0 && (
          <li aria-hidden className="my-2 border-t border-rule" />
        )}
        {visibleRows.map(row => {
          const l = row.list
          const moves = listMoveTargets(lists, l.id)
          const parent = moves.outdent
          return (
            <li key={l.id}>
              {renamingId === l.id ? (
                <div
                  style={{
                    paddingLeft: row.depth * INDENT + (nested ? 16 : 0),
                  }}
                >
                  <RenameForm
                    initial={l.name}
                    onCancel={() => setRenamingId(null)}
                    onSave={async next => {
                      if (next === l.name) {
                        setRenamingId(null)
                        return
                      }
                      try {
                        await renameList(l.id, next)
                        setRenamingId(null)
                        showToast('Liste omdøbt.')
                      } catch (err: unknown) {
                        showToast(err instanceof Error ? err.message : 'Fejl', {
                          tone: 'signal',
                        })
                      }
                    }}
                  />
                </div>
              ) : (
                <FilterButton
                  active={filter === `l-${l.id}`}
                  count={counts.get(l.id) ?? l.items.length}
                  onClick={() => setFilter(`l-${l.id}`)}
                  title={l.name}
                  depth={row.depth}
                  twisty={nested}
                  folding={
                    row.hasChildren
                      ? {
                          open: !collapsed.has(l.id),
                          toggle: () => setFolded(l.id, !collapsed.has(l.id)),
                        }
                      : undefined
                  }
                  dragging={dragId === l.id}
                  dropAt={drop?.targetId === l.id ? drop.position : null}
                  rowProps={{
                    'data-list-id': l.id,
                    onPointerDown: e => startPointerDrag(e, l.id),
                  }}
                  actions={
                    <Menu
                      align="right"
                      portal
                      trigger={
                        <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
                      }
                      triggerAriaLabel={`Handlinger for ${l.name}`}
                      triggerClassName="p-1"
                    >
                      {close => (
                        <>
                          {moves.up && (
                            <MenuItem
                              icon={<ArrowUp className="h-3.5 w-3.5" />}
                              onClick={() => {
                                close()
                                void handleMove(l.id, moves.up!.id, 'before')
                              }}
                            >
                              Flyt op
                            </MenuItem>
                          )}
                          {moves.down && (
                            <MenuItem
                              icon={<ArrowDown className="h-3.5 w-3.5" />}
                              onClick={() => {
                                close()
                                void handleMove(l.id, moves.down!.id, 'after')
                              }}
                            >
                              Flyt ned
                            </MenuItem>
                          )}
                          {moves.indent && (
                            <MenuItem
                              icon={<CornerDownRight className="h-3.5 w-3.5" />}
                              title={moves.indent.name}
                              onClick={() => {
                                close()
                                void handleMove(
                                  l.id,
                                  moves.indent!.id,
                                  'inside'
                                )
                              }}
                            >
                              Læg under »{moves.indent.name}«
                            </MenuItem>
                          )}
                          {parent && (
                            <MenuItem
                              icon={<CornerLeftUp className="h-3.5 w-3.5" />}
                              title={parent.name}
                              onClick={() => {
                                close()
                                void handleMove(l.id, parent.id, 'after')
                              }}
                            >
                              Flyt ud af »{parent.name}«
                            </MenuItem>
                          )}
                          {(moves.up ||
                            moves.down ||
                            moves.indent ||
                            parent) && <MenuSeparator />}
                          <MenuItem
                            icon={<Pencil className="h-3.5 w-3.5" />}
                            onClick={() => {
                              close()
                              setRenamingId(l.id)
                            }}
                          >
                            Omdøb
                          </MenuItem>
                          <MenuSeparator />
                          <MenuItem
                            danger
                            icon={<Trash2 className="h-3.5 w-3.5" />}
                            onClick={() => {
                              close()
                              void handleDelete(l)
                            }}
                          >
                            Slet liste
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  }
                >
                  {l.name}
                </FilterButton>
              )}
            </li>
          )
        })}
        {dragId !== null && (
          <li
            aria-hidden
            data-testid="list-drop-end"
            className="relative h-6"
            data-list-drop-end
          >
            {drop?.targetId === null && (
              <DropLine left={nested ? 24 : 8} edge="top" />
            )}
          </li>
        )}
      </ul>

      {/* Portal: the sliding sidebar panel is transformed and clips `fixed`. */}
      {drag &&
        createPortal(
          <div
            aria-hidden
            className="pointer-events-none fixed z-50 max-w-[14rem] truncate rounded-md border border-rule-strong bg-paper px-2.5 py-1 text-sm font-semibold text-ink shadow-md"
            style={{ left: drag.x + 12, top: drag.y + 10 }}
          >
            {lists.find(l => l.id === drag.id)?.name}
          </div>,
          document.body
        )}

      {lists.length > 1 && (
        <p className="mt-2 hidden px-2.5 text-[12px] leading-snug text-ink-400 [@media(hover:hover)]:block">
          Træk en liste for at flytte den eller lægge den ind under en anden.
        </p>
      )}

      <div className="mt-4 border-t border-rule pt-4">
        <WatchlistCreateListInline inputId="new-list" onCreate={handleCreate} />
      </div>
    </nav>
  )
}

const FilterButton: React.FC<{
  active: boolean
  count: number
  onClick: () => void
  children: React.ReactNode
  actions?: React.ReactNode
  title?: string
  /** Nesting level, 0 at the top. */
  depth?: number
  /** Reserve the fold-arrow column so names line up once lists nest. */
  twisty?: boolean
  folding?: { open: boolean; toggle: () => void }
  dragging?: boolean
  dropAt?: ListDropPosition | null
  rowProps?: React.HTMLAttributes<HTMLDivElement> & { 'data-list-id'?: number }
}> = ({
  active,
  count,
  onClick,
  children,
  actions,
  title,
  depth = 0,
  twisty = false,
  folding,
  dragging = false,
  dropAt = null,
  rowProps,
}) => {
  const indent = depth * INDENT
  // Drop lines start where the name starts, so the level reads at a glance.
  const lineLeft = indent + (twisty ? 24 : 8)
  return (
    <div
      {...rowProps}
      className={`group relative flex select-none items-center rounded-sm transition-opacity [-webkit-touch-callout:none] ${
        dropAt === 'inside'
          ? 'bg-paper-200 ring-1 ring-inset ring-ink'
          : active
            ? 'bg-paper-200'
            : 'hover:bg-paper-200/50'
      } ${dragging ? 'opacity-40' : ''}`}
    >
      {dropAt === 'before' && <DropLine left={lineLeft} edge="top" />}
      {dropAt === 'after' && <DropLine left={lineLeft} edge="bottom" />}
      {indent > 0 && (
        <span aria-hidden className="shrink-0" style={{ width: indent }} />
      )}
      {folding ? (
        <button
          type="button"
          onClick={folding.toggle}
          aria-expanded={folding.open}
          aria-label={`${folding.open ? 'Fold' : 'Fold ud'}: ${title ?? ''}`}
          className="ml-1 flex h-6 w-4 shrink-0 items-center justify-center text-ink-400 hover:text-ink"
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform duration-150 ${folding.open ? 'rotate-90' : ''}`}
            aria-hidden
          />
        </button>
      ) : (
        twisty && <span aria-hidden className="ml-1 w-4 shrink-0" />
      )}
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={`flex min-w-0 flex-1 items-center ${twisty ? 'pl-1.5' : 'pl-2.5'} py-1.5 pr-0 text-sm tracking-wide transition-colors ${
          active ? 'text-ink font-semibold' : 'text-ink-500 hover:text-ink'
        }`}
      >
        <span className="min-w-0 flex-1 truncate text-left" title={title}>
          {children}
        </span>
        <span className="w-7 shrink-0 text-right tabular-nums text-xs text-ink-400">
          {count}
        </span>
      </button>
      <div className="w-7 shrink-0 flex items-center justify-end pr-1 [&_button[aria-haspopup]]:opacity-0 [&_button[aria-haspopup]]:transition-opacity group-hover:[&_button[aria-haspopup]]:opacity-100 group-focus-within:[&_button[aria-haspopup]]:opacity-100 [@media(hover:none)]:[&_button[aria-haspopup]]:opacity-100">
        {actions}
      </div>
    </div>
  )
}

/** Where a dragged list will land: a rule with a ring at its start. */
function DropLine({ left, edge }: { left: number; edge: 'top' | 'bottom' }) {
  return (
    <span
      aria-hidden
      data-testid="list-drop-line"
      className={`pointer-events-none absolute right-1 z-10 h-0.5 rounded-full bg-ink ${
        edge === 'top' ? '-top-[2px]' : '-bottom-[2px]'
      }`}
      style={{ left }}
    >
      <span className="absolute -left-1.5 -top-[3px] h-2 w-2 rounded-full border-2 border-ink bg-paper" />
    </span>
  )
}

function RenameForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: string
  onSave: (next: string) => Promise<void>
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const [busy, setBusy] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const next = value.trim()
    if (!next) return
    setBusy(true)
    try {
      await onSave(next)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex items-center gap-1 px-2.5 py-1"
    >
      <input
        type="text"
        value={value}
        autoFocus
        disabled={busy}
        onChange={e => setValue(e.target.value)}
        onBlur={() => {
          if (value.trim() === initial) onCancel()
        }}
        aria-label="Listenavn"
        className="flex-1 min-w-0 border-b border-ink bg-paper text-sm text-ink py-1 focus:outline-none"
      />
      <button
        type="submit"
        disabled={busy || !value.trim()}
        className="text-xs text-signal hover:text-signal-700 font-medium px-1"
      >
        Gem
      </button>
      <button
        type="button"
        onClick={onCancel}
        aria-label="Annuller omdøbning"
        className="text-ink-500 hover:text-ink p-1"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </form>
  )
}

export default WatchlistListNav
